import PropTypes from "prop-types";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import "./Tooltip.scss";

/**
 * Tooltip component that displays additional information when hovering over a target element.
 * Uses `react-tooltip` for functionality and attaches to an element via `targetId`.
 *
 * @param {Object} props - Component props.
 * @param {string} props.label - The text content to display inside the tooltip.
 * @param {string} props.targetId - The ID of the element the tooltip should be attached to.
 * @param {string} [props.position='top'] - The placement of the tooltip. Accepted values: 'top', 'bottom', 'left', 'right'.
 * @param {string} [props.className=''] - Additional custom CSS class for styling the tooltip.
 * @param {string} [props.variant='dark'] - The variant (style) of the tooltip. Accepted values: 'dark', 'light'.
 * @param {number} [props.opacity=1] - The opacity level of the tooltip. Values range from 0 to 1.
 * @param {number} [props.delayShow=0] - The delay in milliseconds before the tooltip appears.
 * @param {boolean} [props.isOpen] - A boolean to control the visibility of the tooltip.
 *
 * @returns {JSX.Element} The Tooltip component.
 */
export const Tooltip = ({ isOpen, label, targetId,
    position = "top", className = "react-tooltip", variant = "dark",
    opacity = 1, delayShow = 0 }) => {

    return (
        <ReactTooltip
            anchorSelect={`#${targetId}`} // Attach to the element with this ID
            place={position} // Tooltip placement (top, bottom, left, right)
            className={className} // Custom CSS classes
            variant={variant} // Tooltip theme (dark or light)
            opacity={opacity} // Opacity level
            delayShow={delayShow} // Delay before showing tooltip
            isOpen={isOpen} // Manually control visibility
            style={{ zIndex: 9999 }}
            content={label} // Tooltip content
        />
    );
};

// Define PropTypes for validation
Tooltip.propTypes = {
    label: PropTypes.string.isRequired,
    targetId: PropTypes.string.isRequired,
    position: PropTypes.oneOf(["top", "bottom", "left", "right"]),
    className: PropTypes.string,
    variant: PropTypes.oneOf(["dark", "light"]),
    opacity: PropTypes.number,
    delayShow: PropTypes.number,
    isOpen: PropTypes.bool,
};
